import React, { useEffect, useMemo } from 'react';
import { AlertCircle, RefreshCw, Loader2 } from '../icons';
// KPI + table + chip icons: the exact Phosphor glyphs Figma uses.
import {
  Play,
  Chats,
  Scales,
  Gauge,
  TrendUp,
  TrendDown,
  ChartLineUp,
  Users,
  ChartBar,
  ChartDonut,
  type Icon,
} from '@phosphor-icons/react';
import { DataTable } from '../components/common/DataTable';
import { EmptyState } from '../components/common/EmptyState';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import { ChartContainer, ChartTooltip } from '../components/ui/chart';
import type { ChartConfig } from '../components/ui/chart';
import { SENTIMENT_CREATOR_PAGE_SIZE, useSentimentReport } from '@discovr/features';
import { TIME_PERIOD_OPTIONS } from './reportingWindow';
import type { TimePeriod } from './reportingWindow';
import { REPORT_SWITCHING_CLASS, isReportLoadFailed, isReportRefreshFailed } from './reportLoadState';
import { ReportRefreshNotice } from './ReportRefreshNotice';
import { EMPTY_VALUE, formatSentimentScore } from './reportFormat';
import { roundInt, round1, sentimentViewFromReport } from './sentimentReportView';
import type { SentimentCreatorRow } from './sentimentReportView';
import { perCreatorColumns } from './sentimentCreatorColumns';
import { useSentimentRange } from '../campaign-detail/useSentimentRange';
import { useCreatorOffset } from './useCreatorOffset';
import { CustomTooltip, TrackingDot } from './sentimentChartParts';
import { usePublishShareWindow } from '../campaign-detail/hooks/usePublishShareWindow';

// Figma "Sentiment Over Time" (1672:33415): tracking line = brand violet, Negative =
// light-red bars. Colours stay raw hex (data-viz, exempt from token rules).
const sentimentChartConfig: ChartConfig = {
  tracking: { label: 'Tracking score', color: 'var(--brand-600)' },
  neg_pct: { label: 'Negative', color: '#FCA5A5' },
};

const CHART_UNAVAILABLE_TEXT = 'Sentiment over time is unavailable right now.';
const NEG_BAR_FILL = '#FEE2E2'; // Figma negative bars (Rectangle fill)

// Same shared options as General Insights' dropdown (reportingWindow.ts's TIME_PERIOD_OPTIONS).
const CHART_WINDOW_OPTIONS = TIME_PERIOD_OPTIONS;

type ChartWindowId = TimePeriod;

interface SentimentAnalysisTabProps {
  /** Campaign whose sentiment report is read. Required for live data. */
  campaignId?: string;
}

// ── Visual constants ──────────────────────────────────────────────────────────

// Emotion bar colours — Figma "Emotional Profile" (1672:33415) confirms
// joy/trust/anticipation/surprise; the rest keep a sensible Plutchik palette.
const EMOTION_COLORS: Record<string, string> = {
  joy: '#15803D',
  trust: '#2E7CF6',
  anticipation: '#E08A1E',
  surprise: '#6366F1',
  fear: '#9647F8',
  sadness: '#737373',
  anger: '#DC2626',
  disgust: '#0EA5E9',
};

// The tab shows only the top of each topic list; the backend sends (and the
// Excel sheet lists) every filtered topic.
const RISING_TOPICS_DISPLAY_LIMIT = 5;
const DECLINING_TOPICS_DISPLAY_LIMIT = 3;

// Axis shape of the empty chart shell.
const DAY_MS = 86_400_000;
const EMPTY_AXIS_MAX_TICKS = 7;
// While the report's own range isn't known yet: the last 7 calendar days.
const EMPTY_AXIS_FALLBACK_SPAN_DAYS = 6;

// ── Helpers ───────────────────────────────────────────────────────────────────

const Card: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <div className={`rounded-lg border border-border bg-card ${className ?? ''}`}>{children}</div>
);

// Figma KPI card (node 1672:33444): stone-100 round icon box on the left, label
// + value on the right. `children` renders the value(s) so each card can colour
// them as needed (e.g. the Pos/Neg split). No period delta (matches GI).
function KpiCard({ icon: IconCmp, label, children }: { icon: Icon; label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-[18px]">
      <div className="flex size-12 shrink-0 items-center justify-center rounded-full bg-stone-100">
        <IconCmp size={30} weight="duotone" className="text-foreground" />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <p className="truncate text-body-sm-regular text-foreground">{label}</p>
        {children}
      </div>
    </div>
  );
}

/** Format an ISO timestamp for the "last aggregated at" footer. */
const fmtRelativeOrAbsolute = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// ── State banners ─────────────────────────────────────────────────────────────

const Banner: React.FC<{
  variant: 'loading' | 'error' | 'empty' | 'collecting';
  title: string;
  subtitle?: string;
  onRetry?: () => void;
}> = ({ variant, title, subtitle, onRetry }) => {
  const tone =
    variant === 'error'
      ? 'border-red-200 bg-red-50 text-red-800'
      : variant === 'collecting'
        ? 'border-amber-200 bg-amber-50 text-amber-800'
        : 'border-border bg-stone-50 text-muted-foreground';
  const IconCmp = variant === 'loading' ? Loader2 : AlertCircle;
  return (
    <div className={`flex items-center gap-3.5 rounded-xl border px-6 py-5 ${tone}`}>
      <IconCmp size={20} className={variant === 'loading' ? 'animate-spin' : undefined} />
      <div className="flex-1">
        <div className="text-[14px] font-semibold">{title}</div>
        {subtitle && <div className="mt-0.5 text-[13px] opacity-80">{subtitle}</div>}
      </div>
      {variant === 'error' && onRetry && (
        <Button type="button" variant="outline" size="sm" onClick={onRetry}>
          <RefreshCw size={12} className="size-3" /> Retry
        </Button>
      )}
    </div>
  );
};

// Chart-head legend + window dropdown, shared by the live + empty chart cards.
const ChartHead: React.FC<{
  selectedWindow: ChartWindowId;
  onWindow: (w: ChartWindowId) => void;
}> = ({ selectedWindow, onWindow }) => (
  <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
    <div className="flex flex-col gap-1">
      <h3 className="text-[16px] font-semibold leading-none text-foreground">Sentiment Over Time</h3>
      <p className="text-body-sm-regular text-muted-foreground">Daily sentiment across this campaign</p>
    </div>
    <div className="flex flex-col items-end gap-3">
      <div className="flex flex-wrap items-center justify-end gap-4 text-[12px] font-medium text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <span className="size-2 rounded-full border border-(--brand-600) bg-card" /> Creator post event
        </span>
        <span className="flex items-center gap-1.5">
          <span className="size-2 rounded-full" style={{ backgroundColor: 'var(--brand-600)' }} /> Tracking score
        </span>
        <span className="flex items-center gap-1.5">
          <span className="size-2 rounded-full" style={{ backgroundColor: '#FCA5A5' }} /> Negative
        </span>
      </div>
      <div className="flex items-center gap-2">
        <Select value={selectedWindow} onValueChange={(v) => onWindow(v as ChartWindowId)}>
          <SelectTrigger
            size="sm"
            className="h-8 gap-1.5 rounded-lg px-3 text-[12px] font-medium text-foreground shadow-none"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent align="end">
            {CHART_WINDOW_OPTIONS.map((o) => (
              <SelectItem key={o.id} value={o.id} className="text-[12px]">
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  </div>
);

/** UTC-midnight timestamps for the empty chart's axis ticks, spread over the
 *  report's range (or the last week while the range is unknown). */
function emptyAxisTicks(from: string | undefined, to: string | undefined, nowMs: number): number[] {
  const toMs = to ? Date.parse(`${to}T00:00:00Z`) : nowMs;
  const fromMs = from ? Date.parse(`${from}T00:00:00Z`) : toMs - EMPTY_AXIS_FALLBACK_SPAN_DAYS * DAY_MS;
  const days = Math.max(1, Math.round((toMs - fromMs) / DAY_MS));
  const count = Math.min(days + 1, EMPTY_AXIS_MAX_TICKS);
  const step = days / Math.max(count - 1, 1);
  return Array.from({ length: count }, (_, i) => fromMs + Math.round(i * step) * DAY_MS);
}

// ── Main component ────────────────────────────────────────────────────────────

export const SentimentAnalysisTab: React.FC<SentimentAnalysisTabProps> = ({
  campaignId,
}) => {
  // Window selection is URL-owned: ?win= picks the preset.
  const { preset: selectedWindow, range, setPreset } = useSentimentRange();
  // Paging restarts at the first page whenever the range changes.
  const rangeKey = `${range.from ?? ''}|${range.to}`;
  const [creatorOffset, setCreatorOffset] = useCreatorOffset(rangeKey);
  // This component is only ever mounted while Sentiment is the active
  // sub-tab (CampaignOverviewTab conditionally renders it) — so publishing
  // unconditionally here is safe and exactly tracks "Sentiment is on screen"
  // via mount/unmount. See usePublishShareWindow's doc comment for why
  // CampaignOverviewTab's own call must use `null` (not this component's
  // resolved value) whenever Sentiment is the active sub-tab, to avoid a
  // publish-order race between the two.
  usePublishShareWindow(selectedWindow);
  const query = useSentimentReport(campaignId ?? '', {
    ...range,
    creatorLimit: SENTIMENT_CREATOR_PAGE_SIZE,
    creatorOffset,
  });
  const report = query.data;
  const refreshFailed = isReportRefreshFailed(query);
  const isSwitchingPeriod = query.isPlaceholderData;
  const retryReport = () => void query.refetch({ cancelRefetch: false });
  const chartHead = <ChartHead selectedWindow={selectedWindow} onWindow={setPreset} />;
  const refreshNotice = refreshFailed ? <ReportRefreshNotice onRetry={retryReport} /> : null;
  // Only an 'ok' report has a view; 'no_data' and 'unavailable' render the
  // tab's empty and error states below.
  const view = useMemo(() => (report?.status === 'ok' ? sentimentViewFromReport(report) : null), [report]);
  const timeline = useMemo(() => view?.timeline ?? [], [view]);

  const creatorTotal = report?.creator_tracking_total ?? 0;
  const isCreatorPageEmpty = report?.status === 'ok' && !query.isPlaceholderData && report.creators.length === 0;
  const lastPageOffset = Math.max(0, Math.floor((creatorTotal - 1) / SENTIMENT_CREATOR_PAGE_SIZE) * SENTIMENT_CREATOR_PAGE_SIZE);
  useEffect(() => {
    if (!isCreatorPageEmpty || creatorOffset === 0 || lastPageOffset === creatorOffset) return;
    setCreatorOffset(lastPageOffset);
  }, [isCreatorPageEmpty, creatorOffset, lastPageOffset, setCreatorOffset]);

  // Pinned tick set so the first + last day are always labelled.
  const xAxisTicks = useMemo(() => {
    if (timeline.length <= 7) return timeline.map((p) => p.date);
    const target = 7;
    const step = (timeline.length - 1) / (target - 1);
    const indices = Array.from({ length: target }, (_, i) => Math.round(i * step));
    return Array.from(new Set(indices))
      .map((i) => timeline[i]?.date)
      .filter((d): d is string => Boolean(d));
  }, [timeline]);

  // Placeholder X axis ticks for empty-state chart shells.
  const rangeFrom = report?.range?.from;
  const rangeTo = report?.range?.to;
  const emptyXTicks = useMemo(
    // eslint-disable-next-line react-hooks/purity -- the fallback axis before the report's range is known ends at wall-clock now.
    () => emptyAxisTicks(rangeFrom, rangeTo, Date.now()),
    [rangeFrom, rangeTo],
  );

  const fmtEmptyTick = (ts: number): string => {
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  };

  // recharts (v3.8.1) silently renders zero axis ticks — CartesianAxis bails
  // out via its `width/height <= 0` guard — for a truly empty `data={[]}`
  // array in some layouts. Passing one real record per tick (with no
  // plottable values) gives it something to lay out against without drawing
  // anything, sidestepping the bug while keeping the chart visually empty.
  const emptyChartData = emptyXTicks.map((t) => ({ __t: t }));

  // Empty chart shell — axes always render, even with nothing to plot. Reused
  // both standalone (no live creator yet) and inline within the populated
  // Card (selected window has no data points but the campaign has data
  // elsewhere), so it does NOT own its own Card/ChartHead wrapper.
  const emptyChartShell = (opts: { icon: Icon; title: string; subtitle?: string }) => (
    <div className="sm:relative sm:h-[280px]">
      <div className="relative h-[180px] sm:h-full">
        <ChartContainer config={sentimentChartConfig} className="size-full">
          <ComposedChart data={emptyChartData} margin={{ top: 8, right: 4, left: 4, bottom: 0 }}>
            <CartesianGrid stroke="var(--color-stone-200)" vertical={false} />
            <XAxis
              type="number"
              dataKey="__t"
              domain={[emptyXTicks[0] ?? 0, emptyXTicks[emptyXTicks.length - 1] ?? 1]}
              ticks={emptyXTicks}
              tickFormatter={fmtEmptyTick}
              tick={{ fontSize: 12, fill: 'var(--muted-foreground)' }}
              tickLine={false}
              axisLine={false}
            />
            {/* recharts v3.8.1 renders zero Y-axis tick labels in this shell no
                matter what combination of explicit ticks/domain/scale/graphical
                items it's given (verified empirically — CartesianGrid and the
                X-axis both render correctly under the same chart, so this isn't
                a container-sizing issue). `width` still reserves the gutter;
                `tick={false}` suppresses the native (broken/absent) labels in
                favor of the manually-positioned column below. */}
            <YAxis width={32} axisLine={false} tickLine={false} tick={false} domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} />
          </ComposedChart>
        </ChartContainer>
        <div className="pointer-events-none absolute inset-y-2 left-1 bottom-6 flex w-8 flex-col justify-between text-right">
          {[100, 75, 50, 25, 0].map((v) => (
            <span key={v} className="text-[12px] leading-none text-muted-foreground">
              {v}
            </span>
          ))}
        </div>
      </div>
      <div className="pointer-events-none flex flex-col items-center justify-center gap-3 pt-4 sm:absolute sm:inset-0 sm:pt-0">
        <div className="flex size-16 items-center justify-center rounded-full bg-muted text-muted-foreground">
          <opts.icon size={28} />
        </div>
        <div className="space-y-1 text-center">
          <p className="text-body-md-semibold text-foreground m-0">{opts.title}</p>
          {opts.subtitle && (
            <p className="max-w-[280px] text-body-sm-regular text-muted-foreground m-0">{opts.subtitle}</p>
          )}
        </div>
      </div>
    </div>
  );

  const emptyChartCard = (
    <Card className="px-6 py-5">
      {chartHead}
      {emptyChartShell({
        icon: ChartLineUp,
        title: 'Sentiment starts tracking at launch',
        subtitle: 'The Sentiment agent scores every comment the moment content goes live. Your trend line builds within hours.',
      })}
    </Card>
  );

  // ── Render branches ────────────────────────────────────────────────────────

  if (!campaignId) {
    return (
      <Banner
        variant="empty"
        title="No campaign selected"
        subtitle="Sentiment data is scoped to a single campaign."
      />
    );
  }

  if (query.isLoading) {
    return (
      <div className="flex flex-col gap-5">
        <Banner variant="loading" title="Loading sentiment data…" />
        <div className="grid grid-cols-2 gap-5 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="flex h-[84px] items-center gap-3 rounded-xl border border-border bg-card px-4 py-[18px]">
              <div className="size-12 shrink-0 rounded-full bg-muted" />
              <div className="flex min-w-0 flex-1 flex-col gap-2">
                <div className="h-3 w-1/2 animate-pulse rounded-sm bg-muted" />
                <div className="h-7 w-2/5 animate-pulse rounded-md bg-muted" />
              </div>
            </div>
          ))}
        </div>
        <div className="h-[360px] rounded-xl border border-border bg-card" />
      </div>
    );
  }

  if (isReportLoadFailed(query) || report?.status === 'unavailable') {
    return (
      <Banner
        variant="error"
        title="Couldn't load sentiment data"
        subtitle="Please try again in a moment."
        onRetry={retryReport}
      />
    );
  }

  // No creator live yet, or live but the rollup hasn't run: the chart shell,
  // not a blocking banner.
  if (!view) {
    return (
      <div className={`flex flex-col gap-5 ${isSwitchingPeriod ? REPORT_SWITCHING_CLASS : ''}`} aria-busy={isSwitchingPeriod}>
        {refreshNotice}
        {emptyChartCard}
      </div>
    );
  }

  const { headline, totals, creators, topicsRising, topicsDeclining, emotions } = view;
  const emotionEntries = Object.entries(emotions);

  return (
    <div className={`flex flex-col gap-5 ${isSwitchingPeriod ? REPORT_SWITCHING_CLASS : ''}`} aria-busy={isSwitchingPeriod}>
      {refreshNotice}
      {/* ── KPI Cards (Figma 1672:33443) — exact Phosphor glyphs, duotone ─── */}
      <div className="grid grid-cols-2 gap-5 lg:grid-cols-4">
        <KpiCard icon={Gauge} label="Sentiment Index">
          <p className="text-[30px] font-medium leading-none text-foreground">
            {headline ? formatSentimentScore(headline.sentiment_index) : EMPTY_VALUE}
          </p>
        </KpiCard>
        <KpiCard icon={Play} label="Risk Index">
          <p className="text-[30px] font-medium leading-none text-foreground">
            {headline ? `${round1(headline.risk_percent)}%` : EMPTY_VALUE}
          </p>
        </KpiCard>
        <KpiCard icon={Chats} label="Comments analyzed">
          <p className="text-[30px] font-medium leading-none text-foreground">
            {totals.comments_analyzed.toLocaleString()}
          </p>
        </KpiCard>
        <KpiCard icon={Scales} label="Pos/Neg Split">
          <p className="text-[24px] font-medium leading-none">
            <span className="text-emerald-700">{roundInt(totals.pos_pct)}%</span>
            <span className="text-muted-foreground"> / </span>
            <span className="text-red-500">{roundInt(totals.neg_pct)}%</span>
          </p>
        </KpiCard>
      </div>

      {/* ── Sentiment Over Time ───────────────────────────────────────────── */}
      <Card className="px-6 py-5">
        {chartHead}
        {report?.chart_status === 'unavailable' ? (
          <p role="status" className="flex h-[280px] items-center justify-center text-body-sm-regular text-muted-foreground">
            {CHART_UNAVAILABLE_TEXT}
          </p>
        ) : timeline.length === 0 ? (
          emptyChartShell({
            icon: ChartLineUp,
            title: 'No daily data points yet',
            subtitle: 'Chart populates as aggregates land.',
          })
        ) : (
          <ChartContainer config={sentimentChartConfig} className="h-[280px] w-full">
            <ComposedChart data={timeline} margin={{ top: 8, right: 4, left: 4, bottom: 0 }} barCategoryGap="30%">
              <CartesianGrid stroke="var(--color-stone-200)" vertical={false} />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 12, fill: 'var(--muted-foreground)' }}
                tickLine={false}
                axisLine={false}
                ticks={xAxisTicks}
                interval={0}
                padding={{ left: 0, right: 0 }}
              />
              <YAxis
                yAxisId="y"
                domain={[0, 100]}
                tick={{ fontSize: 12, fill: 'var(--muted-foreground)' }}
                tickLine={false}
                axisLine={false}
              />
              <ChartTooltip content={<CustomTooltip />} />
              <Bar isAnimationActive={false} yAxisId="y" dataKey="neg_pct" fill={NEG_BAR_FILL} radius={[3, 3, 0, 0]} />
              <Line
                isAnimationActive={false}
                yAxisId="y"
                type="monotone"
                dataKey="tracking"
                stroke="var(--brand-600)"
                strokeWidth={2}
                // Days with no sentiment point are null; bridge the gaps so the
                // SI line is continuous (Figma) instead of invisible isolated dots.
                connectNulls
                // Recharts injects `key` into the dot-renderer's props bag;
                // strip it and pass it explicitly (React 18+ won't spread key).
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                dot={(props: any) => {
                  const { key, ...rest } = props;
                  return <TrackingDot key={key} {...rest} />;
                }}
                activeDot={{ r: 5, fill: 'var(--brand-600)' }}
              />
            </ComposedChart>
          </ChartContainer>
        )}
      </Card>

      {/* ── Per-Creator Sentiment ─────────────────────────────────────────── */}
      <Card className="overflow-hidden">
        <div className="p-5 pb-0">
          <h3 className="text-[18px]/[26px] font-medium text-foreground">Per-Creator Sentiment</h3>
        </div>
        {creators.length === 0 && creatorOffset === 0 ? (
          <EmptyState
            icon={Users}
            title="No live creators yet"
            body="Per-creator scores appear once your approved creators publish."
          />
        ) : (
          <div className="m-5">
            <DataTable<SentimentCreatorRow>
              columns={perCreatorColumns}
              data={creators}
              getRowId={(c) => c.roster_id}
              className="rounded-lg"
              pagination={
                creatorTotal > SENTIMENT_CREATOR_PAGE_SIZE
                  ? {
                      page: Math.floor(creatorOffset / SENTIMENT_CREATOR_PAGE_SIZE) + 1,
                      pageSize: SENTIMENT_CREATOR_PAGE_SIZE,
                      total: creatorTotal,
                      onPageChange: (page) => setCreatorOffset((page - 1) * SENTIMENT_CREATOR_PAGE_SIZE),
                    }
                  : undefined
              }
            />
          </div>
        )}
      </Card>

      {/* ── Topic Clusters + Emotional Profile ─────────────────────────────── */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        {/* Topic Clusters */}
        <Card className="p-5">
          <div className="mb-5">
            <h3 className="text-heading-h6/tight font-medium text-foreground">Topic Clusters</h3>
            <p className="mt-1 text-body-sm-regular text-muted-foreground">
              What audiences are talking about
            </p>
          </div>

          {topicsRising.length === 0 && topicsDeclining.length === 0 ? (
            <EmptyState
              icon={ChartBar}
              title="Nothing to cluster yet"
              body="We group what audiences are talking about once comments come in."
            />
          ) : (
            <>
              <div className="mb-2.5 flex items-center gap-1.5 text-[12px] font-medium uppercase text-muted-foreground">
                <TrendUp size={14} /> {`Rising Topics (Top ${RISING_TOPICS_DISPLAY_LIMIT})`}
              </div>
              <div className="mb-5 flex flex-wrap gap-2">
                {topicsRising.length === 0 ? (
                  <span className="text-[12px] text-muted-foreground">No rising topics detected yet.</span>
                ) : (
                  topicsRising.slice(0, RISING_TOPICS_DISPLAY_LIMIT).map((t) => (
                    <span
                      key={t.topic}
                      title={`Recent: ${t.recent_count} · Prior: ${t.prior_count} · Growth: ${round1(t.growth_rate * 100)}%`}
                      className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-3 py-1 text-[12px] font-medium text-emerald-700"
                    >
                      <TrendUp size={12} /> {t.topic} {t.recent_count}
                    </span>
                  ))
                )}
              </div>

              <div className="mb-2.5 flex items-center gap-1.5 text-[12px] font-medium uppercase text-muted-foreground">
                <TrendDown size={14} /> {`Declining Topics (Top ${DECLINING_TOPICS_DISPLAY_LIMIT})`}
              </div>
              <div className="flex flex-wrap gap-2">
                {topicsDeclining.length === 0 ? (
                  <span className="text-[12px] text-muted-foreground">No declining topics detected yet.</span>
                ) : (
                  topicsDeclining.slice(0, DECLINING_TOPICS_DISPLAY_LIMIT).map((t) => (
                    <span
                      key={t.topic}
                      title={`Recent: ${t.recent_count} · Prior: ${t.prior_count}`}
                      className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-3 py-1 text-[12px] font-medium text-amber-700"
                    >
                      <TrendDown size={12} /> {t.topic} {t.recent_count}
                    </span>
                  ))
                )}
              </div>
            </>
          )}
        </Card>

        {/* Emotional Profile */}
        <Card className="p-5">
          <div className="mb-5">
            <h3 className="text-heading-h6/tight font-medium text-foreground">Emotional Profile</h3>
            <p className="mt-1 text-body-sm-regular text-muted-foreground">
              Plutchik 8-emotion model · latest cumulative
            </p>
          </div>

          {emotionEntries.length === 0 ? (
            <EmptyState
              icon={ChartDonut}
              title="Emotional read pending"
              body="The eight-emotion wheel fills as the Sentiment agent processes reactions."
            />
          ) : (
            <div className="flex flex-col gap-3">
              {emotionEntries.map(([key, percent]) => {
                const pct = roundInt(percent);
                return (
                  <div key={key} className="flex items-center gap-3">
                    <div className="w-[104px] text-[14px] font-medium capitalize text-foreground">
                      {key}
                    </div>
                    <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
                      <div
                        className="h-full rounded-full"
                        style={{ width: `${pct}%`, backgroundColor: EMOTION_COLORS[key] ?? 'var(--muted-foreground)' }}
                      />
                    </div>
                    <div className={`w-10 text-right text-[13px] ${pct > 0 ? 'font-semibold text-foreground' : 'font-medium text-muted-foreground'}`}>
                      {pct}%
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      {/* ── Footer: last aggregated stamp ─────────────────────────────────── */}
      <div className="flex justify-end text-[11px] text-muted-foreground">
        Last aggregated {fmtRelativeOrAbsolute(view.lastAggregatedAt)}
      </div>
    </div>
  );
};
